import React from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuChevronDown, LuCircleAlert, LuCircleCheck, LuInfo, LuTriangleAlert } from 'react-icons/lu';
import { INPUT_CLASS } from '../../../components/form/FormControls';
import { useTranslation } from '../../../i18n/LanguageContext';

// Building blocks shared by the visit modal's steps.

/** Card surface of a block inside a step. */
export const PANEL = [
  'tw-rounded-[1.25rem] tw-border tw-border-slate-200/80 tw-bg-white/80',
  'tw-shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
  'dark:tw-border-white/10 dark:tw-bg-white/[0.03] dark:tw-shadow-none',
].join(' ');

/** Two-column field grid (one column on phones). */
export const FIELD_GRID = 'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2';

/** DOM id of a field's wrapper, so "Eksik: …" chips can jump to it. */
export const fieldAnchor = (key) => `vm-field-${key}`;

/** Scroll a field into view and focus its first control. */
export const focusField = (key) => {
  const element = document.getElementById(fieldAnchor(key));
  if (!element) return;
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const control = element.matches('input, select, textarea, button')
    ? element
    : element.querySelector('input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)');
  if (control) window.setTimeout(() => control.focus({ preventScroll: true }), 250);
};

const TILE_TONES = {
  slate: 'tw-bg-slate-500/10 tw-text-slate-600 tw-ring-slate-500/20 dark:tw-text-slate-300',
  sky: 'tw-bg-sky-500/10 tw-text-sky-700 tw-ring-sky-500/25 dark:tw-text-sky-300',
  teal: 'tw-bg-teal-500/10 tw-text-teal-700 tw-ring-teal-500/25 dark:tw-text-teal-300',
  emerald: 'tw-bg-emerald-500/10 tw-text-emerald-700 tw-ring-emerald-500/25 dark:tw-text-emerald-300',
  amber: 'tw-bg-amber-500/10 tw-text-amber-700 tw-ring-amber-500/25 dark:tw-text-amber-300',
  rose: 'tw-bg-rose-500/10 tw-text-rose-700 tw-ring-rose-500/25 dark:tw-text-rose-300',
  violet: 'tw-bg-violet-500/10 tw-text-violet-700 tw-ring-violet-500/25 dark:tw-text-violet-300',
  indigo: 'tw-bg-indigo-500/10 tw-text-indigo-700 tw-ring-indigo-500/25 dark:tw-text-indigo-300',
  cyan: 'tw-bg-cyan-500/10 tw-text-cyan-700 tw-ring-cyan-500/25 dark:tw-text-cyan-300',
};

/** "2 eksik" / "Tamam" pill at the right of a block header. */
export const SectionStatus = ({ missingCount, complete }) => {
  const { t } = useTranslation();
  if (missingCount > 0) {
    return (
      <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-amber-400/15 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-amber-800 tw-ring-1 tw-ring-inset tw-ring-amber-500/25 dark:tw-text-amber-200">
        <LuCircleAlert aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {t('visitModal.status.missing', { count: missingCount })}
      </span>
    );
  }
  if (complete) {
    return (
      <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1 tw-rounded-full tw-bg-emerald-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-text-emerald-700 tw-ring-1 tw-ring-inset tw-ring-emerald-500/25 dark:tw-text-emerald-300">
        <LuCheck aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
        {t('visitModal.status.done')}
      </span>
    );
  }
  return null;
};

SectionStatus.propTypes = { missingCount: PropTypes.number, complete: PropTypes.bool };

/**
 * A titled block of fields: a letter (evaluation sections A–J) or an icon tile,
 * a sentence on what goes here, and its own missing/complete status.
 */
export const SectionCard = ({ id, letter, icon: Icon, tone = 'teal', title, hint, missingCount = 0, complete = false, aside, children }) => (
  <section id={id} className={`${PANEL} tw-scroll-mt-28 tw-p-4 sm:tw-p-5`}>
    <header className="tw-mb-4 tw-flex tw-items-start tw-gap-3">
      <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl tw-text-sm tw-font-bold tw-ring-1 tw-ring-inset ${TILE_TONES[tone]}`}>
        {letter || (Icon && <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />)}
      </span>
      <div className="tw-min-w-0 tw-flex-1">
        <h3 className="tw-text-[15px] tw-font-semibold tw-leading-snug tw-text-slate-900 dark:tw-text-white">{title}</h3>
        {hint && <p className="tw-mt-0.5 tw-text-[13px] tw-leading-snug tw-text-slate-500 dark:tw-text-slate-400">{hint}</p>}
      </div>
      {aside}
      <SectionStatus missingCount={missingCount} complete={complete} />
    </header>
    {children}
  </section>
);

SectionCard.propTypes = {
  id: PropTypes.string,
  letter: PropTypes.string,
  icon: PropTypes.elementType,
  tone: PropTypes.oneOf(Object.keys(TILE_TONES)),
  title: PropTypes.string.isRequired,
  hint: PropTypes.node,
  missingCount: PropTypes.number,
  complete: PropTypes.bool,
  aside: PropTypes.node,
  children: PropTypes.node,
};

/** Small "Eksik" tag next to the label of an empty required field. */
export const MissingTag = () => {
  const { t } = useTranslation();
  return (
    <span className="tw-shrink-0 tw-rounded-full tw-bg-amber-400/20 tw-px-1.5 tw-py-px tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-text-amber-800 dark:tw-text-amber-200">
      {t('visitModal.common.missing')}
    </span>
  );
};

/**
 * Single choice shown as pills (radio group). With `allowClear`, pressing the
 * chosen pill again empties the value - what the old "Seçiniz" option did.
 */
export const ChoiceChips = ({ label, options, value, onChange, disabled, allowClear }) => (
  <div role="radiogroup" aria-label={label} className="tw-flex tw-flex-wrap tw-gap-2">
    {options.map((option) => {
      const selected = value === option.value;
      const Icon = option.icon;
      return (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={selected}
          disabled={disabled}
          onClick={() => {
            if (selected && !allowClear) return;
            onChange(selected ? '' : option.value);
          }}
          className={`tw-inline-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-full tw-border tw-px-4 tw-text-sm tw-font-semibold tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 disabled:tw-cursor-not-allowed disabled:tw-opacity-60 ${
            selected
              ? 'tw-border-teal-500/40 tw-bg-teal-500/15 tw-text-teal-800 dark:tw-border-teal-400/30 dark:tw-text-teal-200'
              : 'tw-border-slate-200 tw-bg-white/70 tw-text-slate-600 enabled:hover:tw-bg-white enabled:hover:tw-text-slate-900 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-300 dark:enabled:hover:tw-bg-white/[0.08] dark:enabled:hover:tw-text-white'
          }`}
        >
          {selected ? <LuCheck aria-hidden="true" className="tw-h-4 tw-w-4" /> : Icon && <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />}
          {option.label}
        </button>
      );
    })}
  </div>
);

ChoiceChips.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    icon: PropTypes.elementType,
  })).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  allowClear: PropTypes.bool,
};

/** Evet / Hayır pills writing 'yes' / 'no' ('' when cleared). */
export const YesNo = ({ label, value, onChange, disabled }) => {
  const { t } = useTranslation();
  return (
    <ChoiceChips
      label={label}
      value={value || ''}
      onChange={onChange}
      disabled={disabled}
      allowClear
      options={[
        { value: 'yes', label: t('visitModal.common.yes') },
        { value: 'no', label: t('visitModal.common.no') },
      ]}
    />
  );
};

YesNo.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

/** Native select (with optgroups) in the form controls' glass style. */
export const NativeSelect = ({ className = '', children, ...rest }) => (
  <span className="tw-relative tw-block">
    <select
      className={`${INPUT_CLASS} tw-cursor-pointer tw-appearance-none tw-pr-9 [&_optgroup]:tw-bg-white [&_optgroup]:tw-text-slate-500 [&_option]:tw-bg-white [&_option]:tw-text-slate-800 dark:[&_optgroup]:tw-bg-[#1f232b] dark:[&_optgroup]:tw-text-slate-400 dark:[&_option]:tw-bg-[#1f232b] dark:[&_option]:tw-text-slate-100 ${className}`}
      {...rest}
    >
      {children}
    </select>
    <LuChevronDown aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
  </span>
);

NativeSelect.propTypes = { className: PropTypes.string, children: PropTypes.node };

/** Number input with a unit after the value ("Saat", "dk", "km"). */
export const UnitInput = ({ unit, className = '', ...rest }) => (
  <span className="tw-relative tw-block">
    <input className={`${INPUT_CLASS} tw-pr-16 ${className}`} {...rest} />
    <span className="tw-pointer-events-none tw-absolute tw-right-3.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-xs tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">
      {unit}
    </span>
  </span>
);

UnitInput.propTypes = { unit: PropTypes.string.isRequired, className: PropTypes.string };

const VALUE_TONES = {
  neutral: 'tw-text-slate-700 dark:tw-text-slate-200',
  muted: 'tw-text-slate-400 dark:tw-text-slate-500',
  success: 'tw-text-emerald-700 dark:tw-text-emerald-300',
  danger: 'tw-text-rose-700 dark:tw-text-rose-300',
  accent: 'tw-text-violet-700 dark:tw-text-violet-300',
};

/** A value the user doesn't type: read from the visit or calculated. */
export const ReadValue = ({ children, tone = 'neutral', strong }) => (
  <div
    className={`tw-flex tw-h-10 tw-min-w-0 tw-items-center tw-rounded-xl tw-border tw-border-dashed tw-border-slate-200 tw-bg-slate-50/80 tw-px-3.5 tw-text-sm dark:tw-border-white/10 dark:tw-bg-white/[0.02] ${
      strong ? 'tw-font-bold' : 'tw-font-medium'
    } ${VALUE_TONES[tone]}`}
  >
    <span className="tw-truncate">{children}</span>
  </div>
);

ReadValue.propTypes = { children: PropTypes.node, tone: PropTypes.oneOf(Object.keys(VALUE_TONES)), strong: PropTypes.bool };

/**
 * Checkbox row: a big tap target with a custom box. The native input stays in
 * the DOM (screen readers, keyboard) and passes `checked` to onChange.
 */
export const CheckRow = ({ id, label, checked, onChange, disabled, children }) => (
  <div
    className={`tw-rounded-2xl tw-border tw-transition-colors ${
      checked
        ? 'tw-border-teal-500/30 tw-bg-teal-500/[0.06] dark:tw-border-teal-400/25 dark:tw-bg-teal-400/[0.06]'
        : 'tw-border-slate-200/80 tw-bg-white/70 dark:tw-border-white/10 dark:tw-bg-white/[0.02]'
    }`}
  >
    {/* relative: the visually hidden input must sit inside the scrolling body.
        Positioned against the dialog card instead, focusing it scrolled the card itself. */}
    <label htmlFor={id} className={`tw-relative tw-flex tw-items-start tw-gap-3 tw-px-3.5 tw-py-3 ${disabled ? 'tw-cursor-not-allowed tw-opacity-70' : 'tw-cursor-pointer'}`}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="tw-peer tw-sr-only"
      />
      <span
        aria-hidden="true"
        className={`tw-mt-px tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-md tw-border tw-transition-colors peer-focus-visible:tw-ring-2 peer-focus-visible:tw-ring-teal-500/50 ${
          checked
            ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white'
            : 'tw-border-slate-300 tw-bg-white tw-text-transparent dark:tw-border-white/25 dark:tw-bg-white/[0.04]'
        }`}
      >
        <LuCheck className="tw-h-3.5 tw-w-3.5" strokeWidth={3} />
      </span>
      <span className={`tw-min-w-0 tw-text-sm tw-leading-snug ${checked ? 'tw-text-slate-900 dark:tw-text-white' : 'tw-text-slate-700 dark:tw-text-slate-200'}`}>
        {label}
      </span>
    </label>
    {children && <div className="tw-space-y-2 tw-pb-3 tw-pl-11 tw-pr-3.5">{children}</div>}
  </div>
);

CheckRow.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.node.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

const NOTICE_TONES = {
  info: { box: 'tw-border-sky-500/20 tw-bg-sky-500/[0.06] tw-text-sky-950 dark:tw-text-sky-100', icon: 'tw-text-sky-500', Icon: LuInfo },
  warning: { box: 'tw-border-amber-500/30 tw-bg-amber-400/10 tw-text-amber-950 dark:tw-text-amber-100', icon: 'tw-text-amber-500', Icon: LuTriangleAlert },
  danger: { box: 'tw-border-rose-500/25 tw-bg-rose-500/[0.07] tw-text-rose-950 dark:tw-text-rose-100', icon: 'tw-text-rose-500', Icon: LuCircleAlert },
  success: { box: 'tw-border-emerald-500/25 tw-bg-emerald-500/[0.07] tw-text-emerald-950 dark:tw-text-emerald-100', icon: 'tw-text-emerald-500', Icon: LuCircleCheck },
  accent: { box: 'tw-border-violet-500/20 tw-bg-violet-500/[0.06] tw-text-violet-950 dark:tw-text-violet-100', icon: 'tw-text-violet-500', Icon: LuInfo },
};

/** Banner: an icon, an optional bold title, the text and an optional action. */
export const Notice = ({ tone = 'info', icon, title, children, action }) => {
  const style = NOTICE_TONES[tone];
  const Icon = icon || style.Icon;
  return (
    <div className={`tw-flex tw-flex-col tw-gap-3 tw-rounded-2xl tw-border tw-px-4 tw-py-3 sm:tw-flex-row sm:tw-items-center ${style.box}`}>
      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-start tw-gap-3">
        <Icon aria-hidden="true" className={`tw-mt-0.5 tw-h-[18px] tw-w-[18px] tw-shrink-0 ${style.icon}`} />
        <div className="tw-min-w-0 tw-space-y-0.5 tw-text-sm tw-leading-relaxed">
          {title && <p className="tw-font-semibold">{title}</p>}
          {children && <div className="tw-opacity-90">{children}</div>}
        </div>
      </div>
      {action && <div className="tw-shrink-0 tw-pl-8 sm:tw-pl-0">{action}</div>}
    </div>
  );
};

Notice.propTypes = {
  tone: PropTypes.oneOf(Object.keys(NOTICE_TONES)),
  icon: PropTypes.elementType,
  title: PropTypes.node,
  children: PropTypes.node,
  action: PropTypes.node,
};

/** Icon, a line on why the list is empty and what to do next. */
export const EmptyBlock = ({ icon: Icon, title, text, action }) => (
  <div className={`${PANEL} tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center`}>
    <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
      <Icon aria-hidden="true" className="tw-h-7 tw-w-7" />
    </span>
    <div className="tw-space-y-1">
      <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{title}</p>
      {text && <p className="tw-mx-auto tw-max-w-md tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{text}</p>}
    </div>
    {action}
  </div>
);

EmptyBlock.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  text: PropTypes.node,
  action: PropTypes.node,
};

/** Thin progress bar (0–100). */
export const Meter = ({ percent, tone = 'tw-bg-teal-500', className = '' }) => (
  <span className={`tw-block tw-h-1.5 tw-overflow-hidden tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10 ${className}`}>
    <span className={`tw-block tw-h-full tw-rounded-full tw-transition-[width] tw-duration-500 ${tone}`} style={{ width: `${Math.max(0, Math.min(100, percent))}%` }} />
  </span>
);

Meter.propTypes = { percent: PropTypes.number.isRequired, tone: PropTypes.string, className: PropTypes.string };
